import { toast } from "react-toastify";
import { setTitle } from "../../../../helpers/MetaTag";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { useEffect, useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../../../components/form/Button";
import {
  orderDelete,
  orderList,
  orderOrdering,
  orderStatusChange,
} from "../../../../services/orderService";
import ManageOrderDrawer from "../../../../components/sections/front/Order/Drawer/ManageOrderDrawer";
import dayjs from "dayjs";
import ActionBar from "../../../../components/elements/ActionBar";
import { lngDetect } from "../../../../helpers/lngDetect";
import Badge from "../../../../components/elements/Badge";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useOrganization } from "../../../../context/OrganizationContext";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { t } from "i18next";

const statusList = [
  { name: "processing", value: "processing", type: "checkbox" },
  { name: "shipped", value: "shipped", type: "checkbox" },
  { name: "pickup", value: "pickup", type: "checkbox" },
  { name: "delivered", value: "delivered", type: "checkbox" },
  { name: "canceled", value: "canceled", type: "checkbox" },
  { name: "confirmed", value: "confirmed", type: "checkbox" },
  { name: "return-request", value: "return-request", type: "checkbox" },
  { name: "cancel-request", value: "cancel-request", type: "checkbox" },
  { name: "partial-refund", value: "partial-refund", type: "checkbox" },
  { name: "rejected", value: "rejected", type: "checkbox" },
];

// Payment status options for filtering
const paymentStatusList = [
  { name: "pending", value: "pending", type: "checkbox" },
  { name: "Success", value: "success", type: "checkbox" },
  { name: "failed", value: "failed", type: "checkbox" },
];

const getStatusStyle = (status) => {
  const statusLower = status?.toLowerCase();

  const statusStyles = {
    processing: {
      backgroundColor: "#FEF3C7", // Yellow background
      color: "#D97706", // Dark yellow text
      borderColor: "#F59E0B", // Yellow border
    },
    shipped: {
      backgroundColor: "#DBEAFE", // Blue background
      color: "#1D4ED8", // Dark blue text
      borderColor: "#3B82F6", // Blue border
    },
    pickup: {
      backgroundColor: "#FCE7F3", // Pink background
      color: "#BE185D", // Dark pink text
      borderColor: "#EC4899", // Pink border
    },
    delivered: {
      backgroundColor: "#D1FAE5", // Green background
      color: "#047857", // Dark green text
      borderColor: "#10B981", // Green border
    },
    confirmed: {
      backgroundColor: "#ECFDF5", // Light emerald background - Fresh confirmation color
      color: "#065F46", // Dark emerald text - Strong contrast
      borderColor: "#059669", // Emerald border - Professional accent
    },
    canceled: {
      backgroundColor: "#FEE2E2", // Red background
      color: "#DC2626", // Dark red text
      borderColor: "#EF4444", // Red border
    },
    "return-request": {
      backgroundColor: "#FDF4FF", // Purple background
      color: "#7C2D92", // Dark purple text
      borderColor: "#A855F7", // Purple border
    },
    rejected: {
      backgroundColor: "#F3F4F6", // Gray background
      color: "#374151", // Dark gray text
      borderColor: "#6B7280", // Gray border
    },
  };

  return (
    statusStyles[statusLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

// Payment Status Styling Function
const getPaymentStatusStyle = (status) => {
  const statusLower = status?.toLowerCase();

  const paymentStatusStyles = {
    pending: {
      backgroundColor: "#FEF3C7", // Yellow background
      color: "#D97706", // Dark yellow text
      borderColor: "#F59E0B", // Yellow border
    },
    success: {
      backgroundColor: "#D1FAE5", // Green background
      color: "#047857", // Dark green text
      borderColor: "#10B981", // Green border
    },
    failed: {
      backgroundColor: "#FEE2E2", // Red background
      color: "#DC2626", // Dark red text
      borderColor: "#EF4444", // Red border
    },
  };

  return (
    paymentStatusStyles[statusLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

const OrderList = () => {
  const { orderType } = useParams();
  const navigate = useNavigate();
  const { event, eventLoading, selectedLanguage } = useOutletContext();
  const { organizations, selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [userkeyword, setuserKeyword] = useState("");
  const usersearch = useDebounce(userkeyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "order",
      filters,
      search,
      orderType,
      selectedOrganization,
      event?.data?._id,
    ],
    queryFn: async () => {
      const res = await orderList({
        ...filters,
        ...(orderType &&
          orderType !== "all" && {
            status: orderType === "cancelled" ? "canceled" : "processing",
          }),
        ...(search && { keyword: search }),
        organization: selectedOrganization,
        event: event?.data?._id,
        viewAs: "seller",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!orderType && !!event?.data?._id,
  });

  const tableHeadData = [
    {
      _id: "OrderId",
      name: t("Order Id"),
      align: "left",
    },
    {
      _id: 1,
      name: t("Total Amount"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Event Name"),
      align: "left",
      //   ...tableFilter({
      //     filterKey: "event",
      //     filters,
      //     setFilters: (val) => {
      //       setFilters((pre) => ({ ...pre, ...val }));
      //     },
      //     filterList: eventData?.data?.map((item) => ({
      //       name: lngDetect(item?.title),
      //       value: item?._id,
      //       type: "checkbox",
      //     })),
      //   }),
    },
    {
      _id: 3,
      name: t("Order by"),
      align: "left",
      //   ...tableFilter({
      //     filterKey: "user",
      //     filters,
      //     setFilters: (val) => {
      //       setFilters((pre) => ({ ...pre, ...val }));
      //     },
      //     filterList: userData?.data?.map((item) => ({
      //       name: `${item?.firstName} ${item?.lastName}`,
      //       value: item?._id,
      //       type: "checkbox",
      //     })),
      //   }),
    },
    {
      _id: 4,
      name: t("Order date"),
      align: "left",
      //   ...tableFilter({
      //     content: (
      //       <DatePicker
      //         onChange={({ startDate, endDate }) => {
      //           if (!startDate && !endDate) {
      //             setFilters((pre) => {
      //               delete pre?.startDate;
      //               delete pre?.endDate;
      //               return { ...pre };
      //             });
      //           } else {
      //             setFilters((pre) => ({ ...pre, startDate, endDate }));
      //           }
      //         }}
      //         renge
      //       />
      //     ),
      //   }),
    },
    // {
    //   _id: 5,
    //   name: "Order by",
    //   align: "left",
    //   //   ...tableFilter({
    //   //     filterKey: "user",
    //   //     filters,
    //   //     setFilters: (val) => {
    //   //       setFilters((pre) => ({ ...pre, ...val }));
    //   //     },
    //   //     filterList: userData?.data?.map((item) => ({
    //   //       name: `${item?.firstName} ${item?.lastName}`,
    //   //       value: item?._id,
    //   //       type: "checkbox",
    //   //     })),
    //   //   }),
    // },
    {
      _id: "paymentStatus",
      name: t("payment Status"),
      align: "left",
      ...tableFilter({
        filterKey: "paymentStatus",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...paymentStatusList],
      }),
    },
    {
      _id: 5,
      name: t("Order Status"),
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...statusList],
      }),
    },
    {
      _id: 8,
      name: t("Action"),
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    const statusText =
      item?.status?.info?.charAt(0)?.toUpperCase() +
        item?.status?.info?.slice(1) || "-";
    const statusStyle = getStatusStyle(statusText);

    // Payment status styling
    const paymentStatus = item?.payment?.status || "pending";
    const paymentStatusText =
      paymentStatus.charAt(0)?.toUpperCase() + paymentStatus.slice(1);
    const paymentStatusStyle = getPaymentStatusStyle(paymentStatus);
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.orderId || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: `${
            item?.eventData?.currency?.symbol || "$"
          } ${item?.total.toString()}`,
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.eventData?.title) || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.userData?.firstName || item?.userData?.lastName
              ? `${item?.userData?.firstName ?? ""} ${
                  item?.userData?.lastName ?? ""
                }`
              : "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: dayjs(item?.createdAt).format("MMM DD, YYYY hh:mm A") || "-",
        },
        {
          _id: "paymentStatus",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: paymentStatusStyle.backgroundColor,
                color: paymentStatusStyle.color,
                borderColor: paymentStatusStyle.borderColor,
              }}
            >
              {paymentStatusText}
            </span>
          ),
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
              style={{
                backgroundColor: statusStyle.backgroundColor,
                color: statusStyle.color,
                borderColor: statusStyle.borderColor,
              }}
            >
              {statusText}
            </span>
          ),
        },
        {
          _id: "action",
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: "details",
              name: t("Details"),
              icon: "fa-regular fa-circle-info",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
            // {
            //   _id: "delete",
            //   name: "Delete",
            //   icon: "fa-regular fa-trash-can",
            //   standout: true,
            //   onClick: () => {
            //     setDeleteModal({
            //       isOpen: true,
            //       data: item,
            //     });
            //   },
            // },
          ],
        },
      ],
    };
  });

  // add or edit page =================>
  function pageManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(
            ["order", filters, search, orderType, selectedOrganization],
            (state) => {
              if (state) {
                let stateData = JSON.parse(JSON.stringify(state?.data));
                stateData = [...stateData, res?.data];
                return { ...state, data: stateData };
              }
            }
          );
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries([
              "order",
              filters,
              search,
              orderType,
            ]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(
          ["order", filters, search, orderType, selectedOrganization],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = stateData?.map((item) =>
                item?._id === res?.data?._id ? { ...item, ...res?.data } : item
              );
              return { ...state, data: stateData };
            }
          }
        );
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries([
            "order",
            filters,
            search,
            orderType,
            selectedOrganization,
          ]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   Order delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await orderDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(
          ["order", filters, search, orderType, selectedOrganization],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = stateData?.filter(
                (item) => !payload?.includes(item?._id)
              );

              return { ...state, data: stateData };
            }
          }
        );

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries([
            "order",
            filters,
            search,
            orderType,
            selectedOrganization,
          ]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  const { mutate: handleOrdering, isPending } = useMutation({
    mutationFn: async ({ newData, oldData }) => {
      const res = await orderOrdering({
        ordering_data: newData?.map((item, i) => ({
          id: item?._id,
          ordering: i,
        })),
      });
      return { res, newData, oldData };
    },
    onSuccess: ({ res, newData, oldData }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(
          ["order", filters, search, orderType, selectedOrganization],
          (state) => {
            if (state) {
              return { ...state, data: oldData };
            }
          }
        );
        toast.warning(res?.message || res?.error);
      }
    },
  });

  useEffect(() => {
    if (!eventLoading) {
      if (!orderType || !["all", "pending", "cancelled"].includes(orderType)) {
        return navigate(
          `/${selectedLanguage}/user/myEvent/${event?.data?.uid}`
        );
      }
    }
  }, [eventLoading]);

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-8 py-8 px-5">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">
              {t("Orders")}
            </div>
            <div className="text-sm xl:text-base h-10">
              {t(
                "Easily handle all order actions: update order info, status, and add new order data."
              )}
            </div>
          </div>
          {/* <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-primary/10 hover:!border-primary/10"
              }
              variant="secondary"
              buttonLabel={"Create New"}
              buttonLabelClasses={
                "font-medium !text-sm uppercase whitespace-nowrap"
              }
              buttonHasLink
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div> */}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full md:w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-full md:w-72">
              <Search
                placeholder={t("Search here")}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} {t("Selected")}
                  <button
                    onClick={() => setSelectedItems([])}
                    type="button"
                    className="cursor-pointer"
                  >
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
          {/* <div className="flex items-center gap-3">
            <div className="relative">
              <Select
                selectedValue={"Buyer"}
                dropdownData={[{ name: "Buyer", value: "Buyer" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
            <div className="relative">
              <Select
                selectedValue={"Past 3 month"}
                dropdownData={[{ name: "Past 3 month", value: "Past 3 month" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
          </div> */}
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(
              ["order", filters, search, orderType, selectedOrganization],
              (state) => {
                if (state) {
                  return { ...state, data: val };
                }
              }
            );
          }}
          handleOrdering={(newData, oldData) =>
            handleOrdering({ newData, oldData })
          }
          //   isOrderingDisabled={false}
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          // selectable
        />
      </div>
      <ManageOrderDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={pageManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default OrderList;
